import { 
  ScenarioTemplate,
  TagDefinition,
  CategoryHierarchy,
  ScenarioDependency,
  Scenario,
  GlobalInvestment,
  GlobalIncident
} from '../types/ScenarioManagement';
import { logger } from './LoggingService';

// Local type aliases for compatibility
type ScenarioCategory = CategoryHierarchy;
type ScenarioTag = TagDefinition;

interface ScenarioData {
  scenarios: [string, Scenario][]; // Full Scenario objects
  templates: [string, ScenarioTemplate][];
  categories: [string, ScenarioCategory][];
  tags: [string, ScenarioTag][];
  dependencies: [string, ScenarioDependency][];
  globalInvestments?: [string, GlobalInvestment][]; // Global investments available across all scenarios
  globalIncidents?: [string, GlobalIncident][]; // Global incidents available across all scenarios
}

// interface BackupMetadata {
//   timestamp: string;
//   version: string;
//   checksum: string;
//   recordCount: number;
// }

export class JSONPersistenceService {
  private static instance: JSONPersistenceService;
  private readonly STORAGE_KEY = 'riskGame_scenarioData';
  private readonly BACKUP_PREFIX = 'riskGame_backup_';
  private maxBackups: number = 5;

  private constructor() {}

  static getInstance(): JSONPersistenceService {
    if (!JSONPersistenceService.instance) {
      JSONPersistenceService.instance = new JSONPersistenceService();
    }
    return JSONPersistenceService.instance;
  }

  /**
   * Save scenario data - now only used for temporary in-memory operations
   * Actual persistence is handled through export/import functionality
   */
  async saveData(data: ScenarioData): Promise<void> {
    // No-op: We don't save to localStorage anymore
    // Data modifications are only temporary until exported
    logger.info('Data modifications are temporary. Use export to save changes.', 'JSONPersistenceService');
  }

  /**
   * Load scenario data - always loads from default-scenarios.json
   */
  async loadData(): Promise<ScenarioData | null> {
    try {
      // Always load fresh from default scenarios
      return await this.initializeWithDefaults();
    } catch (error) {
      logger.error('Failed to load scenario data', 'JSONPersistenceService', {}, error as Error);
      return null;
    }
  }

  /**
   * Create backup - deprecated, backups are handled through export
   */
  private async createBackup(): Promise<void> {
    // No-op: Backups are now handled through export functionality
  }

  /**
   * Get list of available backups - deprecated
   */
  async getBackups(): Promise<string[]> {
    // No backups in localStorage anymore
    return [];
  }

  /**
   * Get the latest backup
   */
  private async getLatestBackup(): Promise<string | null> {
    const backups = await this.getBackups();
    return backups.length > 0 ? backups[0] : null;
  }

  /**
   * Restore data from specific backup - deprecated
   */
  async restoreFromBackup(timestamp: string): Promise<ScenarioData> {
    // No backup restoration - use import functionality instead
    throw new Error('Backup restoration is deprecated. Use import functionality instead.');
  }

  /**
   * Clean up old backups - deprecated
   */
  private async cleanupOldBackups(): Promise<void> {
    // No-op: No backups to clean up
  }

  /**
   * Clear and reinitialize - just returns fresh defaults
   */
  async clearAndReinitialize(): Promise<ScenarioData> {
    try {
      // Just return fresh defaults
      return await this.initializeWithDefaults();
    } catch (error) {
      logger.error('Failed to reinitialize data', 'JSONPersistenceService', {}, error as Error);
      throw new Error('Failed to reinitialize data');
    }
  }

  /**
   * Initialize with default scenario data from selector
   */
  async initializeWithDefaults(): Promise<ScenarioData> {
    // Load default scenarios from JSON file
    let defaultScenarios: Scenario[] = [];

    try {
      const defaultScenariosData = await import('../data/default-scenarios.json');
      defaultScenarios = defaultScenariosData.defaultScenarios.map((scenario: any) => ({
        ...scenario,
        category: scenario.category || 'default',
        createdAt: new Date(),
        updatedAt: new Date()
      }));
    } catch (error) {
      logger.error('Failed to load default scenarios from JSON', 'JSONPersistenceService', {}, error as Error);
      // Fallback to a minimal default scenario if JSON loading fails
      defaultScenarios = [{
        id: 'techcorp',
        title: 'TechCorp International',
        subtitle: 'Geographic Expansion & AI Adoption',
        description: 'Navigate cybersecurity challenges as CEO of a Fortune 500 technology company.',
        icon: '🏢',
        difficulty: 'Intermediate',
        duration: '15-20 min',
        isAvailable: true,
        version: 1,
        createdAt: new Date(),
        updatedAt: new Date(),
        category: 'Business',
        tags: ['ai', 'expansion'],
        content: {
          objectives: [],
          successCriteria: [],
          availableResources: [],
          constraints: [],
          availableInvestments: [],
          incidentConfigs: []
        }
      }];
    }

    const data: ScenarioData = {
      scenarios: defaultScenarios.map(scenario => [scenario.id, scenario]),
      templates: [],
      categories: [
        ['business-strategy', { id: 'business-strategy', name: 'Business Strategy', icon: '💼', color: '#3B82F6', description: 'Strategic business decisions', level: 0, scenarioCount: 0 }],
        ['technical-challenges', { id: 'technical-challenges', name: 'Technical Challenges', icon: '⚙️', color: '#10B981', description: 'Technical and infrastructure scenarios', level: 0, scenarioCount: 0 }],
        ['crisis-management', { id: 'crisis-management', name: 'Crisis Management', icon: '🚨', color: '#EF4444', description: 'Handle emergency situations', level: 0, scenarioCount: 0 }],
        ['growth-expansion', { id: 'growth-expansion', name: 'Growth & Expansion', icon: '📈', color: '#F59E0B', description: 'Scale and growth challenges', level: 0, scenarioCount: 0 }]
      ],
      tags: [
        ['ai', { id: 'ai', name: 'AI/ML', color: '#8B5CF6', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['expansion', { id: 'expansion', name: 'Expansion', color: '#0EA5E9', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['ma', { id: 'ma', name: 'M&A', color: '#F97316', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['integration', { id: 'integration', name: 'Integration', color: '#84CC16', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['transformation', { id: 'transformation', name: 'Transformation', color: '#DC2626', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['scaling', { id: 'scaling', name: 'Scaling', color: '#10B981', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['startup', { id: 'startup', name: 'Startup', color: '#F59E0B', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['data-breach', { id: 'data-breach', name: 'Data Breach', color: '#DC2626', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['apt-attack', { id: 'apt-attack', name: 'APT Attack', color: '#991B1B', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['regulatory-compliance', { id: 'regulatory-compliance', name: 'Regulatory Compliance', color: '#4B5563', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['crisis-communication', { id: 'crisis-communication', name: 'Crisis Communication', color: '#EF4444', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['stakeholder-management', { id: 'stakeholder-management', name: 'Stakeholder Management', color: '#3B82F6', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['banking', { id: 'banking', name: 'Banking', color: '#059669', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['compliance', { id: 'compliance', name: 'Compliance', color: '#6B7280', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['trust', { id: 'trust', name: 'Trust', color: '#10B981', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['ransomware', { id: 'ransomware', name: 'Ransomware', color: '#DC2626', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['healthcare', { id: 'healthcare', name: 'Healthcare', color: '#0891B2', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['patient-safety', { id: 'patient-safety', name: 'Patient Safety', color: '#EF4444', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['critical-infrastructure', { id: 'critical-infrastructure', name: 'Critical Infrastructure', color: '#7C3AED', usageCount: 0, isSystemTag: true, createdAt: new Date() }],
        ['public-relations', { id: 'public-relations', name: 'Public Relations', color: '#F59E0B', usageCount: 0, isSystemTag: true, createdAt: new Date() }]
      ],
      dependencies: [],
      globalInvestments: [],
      globalIncidents: []
    };

    // Initialize global investments from the investments data
    const { investments } = await import('../data/investments');
    data.globalInvestments = investments.map(inv => [inv.id, {
      ...inv,
      businessOutcomes: inv.businessOutcomes || [],
      isGlobal: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'System',
      isEnabled: true
    }]);

    // Initialize global incidents from the incidents data
    const { incidents } = await import('../data/incidents');
    data.globalIncidents = incidents.map(inc => [inc.id, {
      id: inc.id,
      title: inc.title,
      icon: '🚨',
      description: inc.description,
      severity: inc.severity,
      category: inc.category,
      timeLimit: inc.timeLimit,
      potentialImpact: inc.potentialImpact,
      responseOptions: inc.responseOptions,
      isGlobal: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'System',
      isEnabled: true,
      tags: ['system', 'default'],
      order: 0
    }]);

    // Don't save to localStorage
    return data;
  }

  /**
   * Validate data structure integrity
   */
  private validateDataStructure(data: ScenarioData): void {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid data structure: not an object');
    }

    const requiredFields = ['scenarios', 'templates', 'categories', 'tags', 'dependencies'];
    for (const field of requiredFields) {
      if (!Array.isArray(data[field as keyof ScenarioData])) {
        throw new Error(`Invalid data structure: ${field} is not an array`);
      }
    }
    
    // globalInvestments is optional but should be an array if present
    if (data.globalInvestments !== undefined && !Array.isArray(data.globalInvestments)) {
      throw new Error('Invalid data structure: globalInvestments is not an array');
    }
  }

  /**
   * Generate simple checksum for data integrity
   */
  private generateChecksum(content: string): string {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(16);
  }

  /**
   * Export scenarios to downloadable JSON
   * This is the primary way to save custom scenarios
   */
  async exportScenarios(scenarioIds: string[], customScenarios?: [string, Scenario][]): Promise<void> {
    try {
      const data = await this.loadData();
      if (!data) throw new Error('No data to export');

      // If custom scenarios are provided, use those; otherwise filter from loaded data
      const scenariosToExport = customScenarios || 
        data.scenarios.filter(([id]) => scenarioIds.includes(id));
      
      if (!scenariosToExport || scenariosToExport.length === 0) {
        throw new Error('No scenarios to export');
      }
      
      const exportData = {
        scenarios: scenariosToExport,
        exportedAt: new Date().toISOString(),
        version: 1,
        type: 'custom-scenarios'
      };
      
      const jsonStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().replace(/:/g, '-');
      const filename = `custom-scenarios-${timestamp}.json`;
      
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      logger.info('Scenarios exported successfully', 'JSONPersistenceService', {
        scenarioCount: scenariosToExport.length,
        filename
      });
    } catch (error) {
      logger.error('Failed to export scenarios', 'JSONPersistenceService', {}, error as Error);
      throw error;
    }
  }

  /**
   * Import scenarios from uploaded file
   */
  async importScenarios(file: File): Promise<number> {
    try {
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);
      
      if (!importData.scenarios || !Array.isArray(importData.scenarios)) {
        throw new Error('Invalid import file format');
      }

      // Since we don't persist to localStorage, imported scenarios are only temporary
      // They would need to be re-imported each session or integrated into default-scenarios.json
      
      logger.warn('Imported scenarios are temporary and will not persist across sessions', 'JSONPersistenceService');
      logger.info('To make scenarios permanent, add them to default-scenarios.json', 'JSONPersistenceService');
      
      return importData.scenarios.length;
    } catch (error) {
      logger.error('Failed to import scenarios', 'JSONPersistenceService', {}, error as Error);
      throw error;
    }
  }

}

export default JSONPersistenceService;